import { Loop, TableEditorConfig, Value } from "../dto";
import { cloneDeep } from "lodash";
import { isContained, isIntersected } from "./rect";
import { getListBindsByLoop } from "./value";
import { isNullOrUndefined } from "../../utils";

interface LoopWithChildren extends Loop {
  children: LoopWithChildren[];
}
/**
 * 给循环配置转换成树形结构并过滤掉非法（相交且不包含）循环 如果是包含关系的话
 * @param loops 循环配置
 * @returns 转换后的循环配置
 */
export function transformLoops(loops: Loop[]): LoopWithChildren[] {
  // 过滤并按 minRow 升序排序（从上到下）
  const sorted = cloneDeep(loops)
    .filter(
      (loop) =>
        !isNullOrUndefined(loop.minRow) && !isNullOrUndefined(loop.maxRow)
    )
    .sort((a, b) => a.minRow - b.minRow);
  // 过滤后的循环
  const filtered = [];
  // 标记需要删除的循环id
  const removedIds = new Set();
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    if (removedIds.has(current.id)) continue; // 跳过已过滤的
    let shouldAdd = true;
    // 检查当前区域是否与之前的区域相交但不包含
    for (let j = 0; j < i; j++) {
      const prev = sorted[j];
      if (
        !removedIds.has(prev.id) && // 未被过滤
        isIntersected(current, prev) && // 相交
        !isContained(current, prev) && // 不包含
        !isContained(prev, current) // 也不被包含
      ) {
        // 当前区域在下（minRow 更大），过滤掉
        shouldAdd = false;
        removedIds.add(current.id);
        console.error(
          `【TableEditor】循环配置冲突，出现相交且不包含的情况，已忽略下方的循环配置，配置id为【${current.id}】`,
          prev,
          current
        );
        break;
      }
    }
    if (shouldAdd) {
      filtered.push(current);
    }
  }
  removedIds.clear();
  // 遍历所有节点，构建父子关系
  for (let i = 0; i < filtered.length; i++) {
    const node = filtered[i];
    node.children = [];
    // 寻找当前节点的所有子节点
    for (let j = 0; j < filtered.length; j++) {
      const potentialChild = filtered[j];
      if (i !== j && isContained(node, potentialChild)) {
        // 检查是否已经有更近的父节点
        let hasCloserParent = false;
        for (let k = 0; k < filtered.length; k++) {
          if (
            k !== i &&
            k !== j &&
            isContained(filtered[k], potentialChild) &&
            isContained(node, filtered[k])
          ) {
            hasCloserParent = true;
            break;
          }
        }
        if (!hasCloserParent) {
          // 子节点的rect更改为相对于父节点的rect
          potentialChild.minRow -= node.minRow;
          potentialChild.maxRow -= node.minRow;
          node.children.push(potentialChild);
          removedIds.add(potentialChild.id);
        }
      }
    }
  }
  // 过滤掉所有子孙节点，只保留顶级节点
  return filtered.filter((node) => !removedIds.has(node.id));
}

interface LoopWithParentId extends Loop {
  parentId?: string;
}
/**
 * 给循环配置增加parentId 如果有包含关系的话
 * @param loops 循环配置
 * @returns 转换后的循环配置
 */
export const loopsWithParentId = (loops: Loop[]): LoopWithParentId[] => {
  const newLoops = cloneDeep(loops);
  // 标记需要删除的循环id
  const removedIds = new Set();
  for (let i = 0; i < newLoops.length; i++) {
    const node = newLoops[i];
    // 寻找当前节点的所有子节点
    for (let j = 0; j < newLoops.length; j++) {
      const potentialChild = newLoops[j];
      if (i !== j && isContained(node, potentialChild)) {
        // 检查是否已经有更近的父节点
        let hasCloserParent = false;
        for (let k = 0; k < newLoops.length; k++) {
          if (
            k !== i &&
            k !== j &&
            isContained(newLoops[k], potentialChild) &&
            isContained(node, newLoops[k])
          ) {
            hasCloserParent = true;
            break;
          }
        }
        if (!hasCloserParent) {
          node.parentId = potentialChild.id;
          removedIds.add(potentialChild.id);
        }
      }
    }
  }
  return newLoops.filter((node) => !removedIds.has(node.id));
};

export const getLoopMeta = (
  rows: TableEditorConfig["rows"],
  loop: Loop,
  dataSource: Value[]
) => {
  // 转换循环配置
  const { bindCodes, bindFields } = getListBindsByLoop(
    loop,
    { rows } as TableEditorConfig,
    dataSource
  );
  // 是否包含循环字段
  let hasLoopField = false;
  // 循环次数
  let loopLength = 0;
  bindCodes.forEach((key) => {
    const item = dataSource.find((i) => i.code === key);
    if (item?.isList) {
      hasLoopField = true;
      loopLength = Math.max(item?.valueList?.length ?? 0, loopLength);
    }
  });
  return {
    hasLoopField,
    loopLength,
    bindCodes,
    bindFields,
  };
};
